import styled from '@emotion/styled'
import * as React from 'react'

import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { Card } from '~ui/components/content/Card/Card'
import { IconGoBack } from '~ui/components/icons/specific/IconChevrons'
import { Box, Grid } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'
import { cn } from '~ui/libs/classnames'
import { durations } from '~ui/styleGuide/styles'

import { CartButtonIcon } from './CartButtonIcon'

type TButtonIconProps = Pick<PropsOf<typeof ButtonIcon>, 'state'>

type TProps = NoChildren &
  TestingProps &
  TButtonIconProps & {
    /**
     * total sum of the cart, with the currency abbreviation
     */
    cartSum: string
    /**
     * the total number of items in the cart
     */
    cartItemCount?: number
    /**
     * cart button click handler
     */
    onCartButtonClick: PropsOf<typeof ButtonIcon>['onClick']
    /**
     * back button icon click handler
     */
    onBackClick: PropsOf<typeof ButtonIcon>['onClick']
    /**
     * translated label of the back button
     */
    backLabel: string
  }

// TODO should the values be props (later)?
const StyledCartButtonContainer = styled(Box)`
  position: absolute;
  transition: transform ${durations.basic}ms;

  &.empty {
    bottom: 0.25rem;
  }

  &.non-empty {
    transform: translateY(-80%);
  }
`

// to set bottom page padding accordingly
export const cartSummaryHeightRem = 4.5

/**
 * displays bottom panel with back button and
 * cart button icon that opens mobile cart and shows number of items in the cart
 */
export const CartSummaryMobile: React.FC<TProps> = ({
  state,
  cartSum,
  cartItemCount = 0,
  backLabel,
  onBackClick,
  onCartButtonClick,
  dataTestId,
}) => {
  const isEmpty = !cartItemCount || cartItemCount <= 0

  return (
    <Card
      height={`${cartSummaryHeightRem}rem`}
      padding={`none`}
      position={`fixed`}
      bottom={0}
      left={0}
      right={0}
      bgColor={`bgColors.card`}
      shadow={`medium`}
      radius={{
        bottomLeft: `none`,
        bottomRight: `none`,
        topLeft: `large`,
        topRight: `large`,
      }}
      // TODO zIndex
      zIndex={2}
    >
      <Grid
        height={`100%`}
        columnGap={`medium`}
        justifyItems={`center`}
        alignContent={`center`}
        gridTemplateColumns={`repeat(3, 1fr)`}
      >
        {/* BACK BUTTON */}
        <Grid>
          <ButtonIcon
            shadow={`none`}
            padding={`none`}
            variant={`ghost`}
            icon={IconGoBack}
            iconSize={`1.5rem`}
            onClick={onBackClick}
          />
          <Box gridRow={2}>
            <Text fontSize={`s12`}>{backLabel}</Text>
          </Box>
        </Grid>

        {/* CART BUTTON */}
        <Grid alignContent={`end`} justifyItems={`center`}>
          <StyledCartButtonContainer
            className={cn({
              'non-empty': !isEmpty,
              empty: isEmpty,
            })}
          >
            <CartButtonIcon
              state={state}
              onClick={onCartButtonClick}
              cartItemCount={cartItemCount}
              dataTestId={dataTestId}
            />
          </StyledCartButtonContainer>
          {!isEmpty && (
            <Box gridRow={2}>
              <Text fontSize={`m16`} fontStyle={`bold`}>
                {cartSum}
              </Text>
            </Box>
          )}
        </Grid>

        {/* // TODO SPACE FOR FUTURE USER-ACCOUNT BUTTON */}
        <Grid />
      </Grid>
    </Card>
  )
}
